import cx from "classnames";
import * as React from "react";
import TooltipWrapper from "../../TooltipWrapper";
import { FormFieldStates, InputProps } from "./exports";

export type QESTextAreaProps = InputProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * A standard text area with a title and optional descriptive text beneath. Its props include all native HTML TextArea attributes, and in particular, adding a maxLength attribute will add a character count underneath.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESTextArea: React.FC<React.PropsWithChildren<QESTextAreaProps>> = ({ containerClassName, descriptiveText, errorText, isValid, label, qesRef, required, testId, validationTooltipContent, wasFocusedExternally, ...props }) => {
  const [hasBeenFocused, setHasBeenFocused] = React.useState(false);
  const [fieldState, setFieldState] = React.useState(FormFieldStates.None);

  React.useEffect(() => {
    if (!isValid && required && hasBeenFocused) {
      setFieldState(FormFieldStates.Error);
    } else {
      setFieldState(FormFieldStates.None);
    }
  }, [isValid, hasBeenFocused, required]);

  React.useEffect(() => {
    if (wasFocusedExternally) {
      setHasBeenFocused(wasFocusedExternally);
    }
  }, [wasFocusedExternally]);

  const subText = React.useMemo(() => {
    if (hasBeenFocused && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused]);

  const characterCount = React.useMemo(() => {
    return (props.value as string)?.length || 0;
  }, [props.value]);

  return (
    // onChange bubbles up when triggered. So changing the textarea is caught by .form-groups onChange,
    // without negating any onChange passed in to the textarea by ...props
    <div className={`qes-text-area__container ${containerClassName}`} key={testId} onBlur={() => setHasBeenFocused(true)}>
      <label htmlFor={props.id || testId} className="qes-text-area__label">
        {label}
        {required ? <span className="text-danger"> *</span> : ""}
        {validationTooltipContent ? <TooltipWrapper containerClassName="ml-3" text={validationTooltipContent}><i className="fa fa-question-circle" /></TooltipWrapper> : ""}
      </label>
      <textarea
        {...props}
        id={props.id || testId}
        className={cx({
          "error": hasBeenFocused && fieldState === FormFieldStates.Error,
          "qes-text-area__textarea": true,
          [props.className]: !!props.className,
        })}
        data-testid={testId}
        required={required}
        ref={qesRef as React.RefObject<HTMLTextAreaElement>}
      />
      <div className="">
        {subText && <small className="qes-text-area__descriptive-text">{subText}</small>}
        {props.maxLength && (
          <small
            className={cx({
              "danger": characterCount >= props.maxLength,
              "qes-text-area__descriptive-text qes-text-area__character-count": true,
            })}
          >
            {(props.maxLength - characterCount).toLocaleString()} / {props.maxLength.toLocaleString()}
          </small>
        )}
      </div>
    </div>
  );
};
